import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../axios';

type Organization = {
    key: string;
    label: string;
    logoUrl?: string;
    plan?: 'TEST' | 'BASE' | 'PRO' | 'ULTIMATE' | 'CUSTOM' | 'INDIVIDUAL';
};

const GET_ORGANIZATIONS_BY_IDS_URL = '/v3-getOrganizationsByIds';

// Lista organizacji użytkownika zmienia się bardzo rzadko (tylko gdy admin doda/usunie
// dostęp), więc cache'ujemy ją w localStorage i wołamy funkcję zasadniczo raz na sesję.
// Klucz zawiera 'auth' i 'org', dzięki czemu logout/reset (userState.logout oraz
// organizationState.reset) czyszczą go automatycznie.
export const ORGANIZATIONS_LS_KEY = 'auth.orgs.list';

type OrganizationsCache = {
  ids: string[];
  data: Organization[];
  ts: number;
};

const sameIdSet = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
};

// Zwraca cache tylko gdy dotyczy dokładnie tego samego zestawu id — inaczej cache miss,
// więc zmiana listy organizacji naturalnie wymusza ponowne pobranie.
const readCache = (ids: string[] | null): OrganizationsCache | null => {
  if (!Array.isArray(ids) || ids.length === 0) return null;
  try {
    const raw = localStorage.getItem(ORGANIZATIONS_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OrganizationsCache;
    if (!parsed?.ids || !Array.isArray(parsed.data)) return null;
    if (!sameIdSet(parsed.ids, ids)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeCache = (ids: string[], data: Organization[]): void => {
  try {
    const payload: OrganizationsCache = { ids, data, ts: Date.now() };
    localStorage.setItem(ORGANIZATIONS_LS_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota */
  }
};

export const useGetOrganizationsByIds = (
  ids: string[] | null,
  options?: UseQueryOptions<Organization[], AxiosError>,
): UseQueryResult<Organization[], AxiosError> => useQuery<Organization[], AxiosError>({
  queryKey: ['organizationsByIds', ids],
  queryFn: async () => {
    const response = await axiosInstance.post<{
                success: boolean;
                message: string;
                data: Organization[];
            }>(GET_ORGANIZATIONS_BY_IDS_URL, { ids });

    const { data } = response.data;
    // Zapis po zalogowaniu / zmianie zestawu organizacji.
    if (Array.isArray(ids) && ids.length > 0) {
      writeCache(ids, data);
    }
    return data;
  },
  enabled: Array.isArray(ids) && ids.length > 0,
  // Hydratacja z localStorage — jeśli mamy cache dla tego zestawu id, funkcja nie jest wołana.
  initialData: () => readCache(ids)?.data,
  initialDataUpdatedAt: () => readCache(ids)?.ts,
  // Lista jest praktycznie niezmienna w obrębie sesji; przy zmianie zestawu id klucz
  // cache się nie zgadza i następuje refetch. Dlatego bez automatycznego odświeżania.
  staleTime: Infinity,
  gcTime: Infinity,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  ...options,
});
